import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { datadogRum } from '@datadog/browser-rum';
import { Toast, ToastMessage } from 'primereact/toast';
import { callAPIAsync } from '../libs/API';
import mixpanel from '../libs/Mixpanel';
import { decryptToken } from '../libs/Cognito';
import { clearStorage } from '../utils/LogOutUtils';
import {
  DecryptedUser,
  Factors,
  ImpersonationRecord,
  Profile,
} from '../shared/types/appTypes';
import { storage } from '../services/StorageService';
import { getDefaultScope } from '../libs/FilterService';
import { ANALYTICS_EXCLUDED_AGENCY_ID } from '../constants';

class NoProfileError extends Error {
  constructor() {
    super('No profile found');
  }
}

/**
 * Manages all authentication and session state for the application.
 *
 * Returns the auth state and actions needed by App.tsx and the header.
 * Handles localStorage hydration, profile fetching, impersonation, and
 * admin route enforcement.
 *
 * @example
 * const {
 *   isAuthenticated, user, profile, impersonation, factors, isLoaded,
 *   handleAuthentication, endImpersonation, clearState, adminCheck,
 * } = useAuth(toast);
 */
export function useAuth(toast: React.RefObject<Toast | null>) {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<DecryptedUser | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [impersonation, setImpersonation] =
    useState<ImpersonationRecord | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [factors, setFactors] = useState<Factors>({} as Factors);

  const displayToastMessage = (toastConfig: ToastMessage) =>
    toast.current && toast.current.show(toastConfig);

  const clearState = useCallback(() => {
    clearStorage();
    setIsAuthenticated(false);
    setUser(null);
    setProfile(null);
    setImpersonation(null);
    setIsLoaded(true);
    setFactors({});
    datadogRum.stopSessionReplayRecording();
    mixpanel.stop_session_recording();
    mixpanel.reset();
  }, []);

  const mixpanelIdentify = useCallback((profileRecord: Profile) => {
    if (profileRecord.id === null) return;
    if (!profileRecord.last_login) {
      mixpanel.alias(
        profileRecord.id.toString(),
        mixpanel.get_distinct_id() as string,
      );
    }
    mixpanel.identify(profileRecord.id.toString());
    mixpanel.people.set_once(profileRecord);
    mixpanel.people.set_once({ $email: profileRecord.email });
    mixpanel.register_once({
      agency_id: profileRecord.agency_id,
      agency_name: profileRecord.agency_name,
    });
    datadogRum.setUser({
      ...profileRecord,
      id: profileRecord.id.toString(),
    });
    if (profileRecord.agency_id !== ANALYTICS_EXCLUDED_AGENCY_ID) {
      datadogRum.startSessionReplayRecording();
      mixpanel.start_session_recording();
    }
  }, []);

  const adminCheck = useCallback(
    (
      isAdminPath: boolean,
      profileRecord: Profile | null,
      impersonationRecord: ImpersonationRecord | null,
    ) => {
      if (isAdminPath) {
        if (impersonationRecord) {
          displayToastMessage({
            severity: 'error',
            summary: 'Admin features are unavailable for impersonated users',
            detail: '',
          });
          return true;
        }
        if (
          ['augintel_admin', 'agency_admin'].indexOf(
            profileRecord?.user_type || '',
          ) < 0
        ) {
          if (import.meta.env.VITE_MIXPANEL_ENABLE === 'false') {
            mixpanel.disable();
          }
          window.location.href = '/';
          return true;
        }
        return false;
      }
      return false;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: displayToastMessage depends on toast ref which is stable
    [],
  );

  // Hydrate auth state from localStorage on mount
  useEffect(() => {
    try {
      const userLocalStorage = storage.getUser();
      const profileTemp = storage.getProfile();
      const impersonationRecord = storage.getImpersonation();
      const factorsRecord = storage.getFactors();
      if (userLocalStorage) {
        const userDecrypted = JSON.parse(
          decryptToken(userLocalStorage.idToken.jwtToken).toString(),
        ) as DecryptedUser;
        if (userDecrypted.auth_time > 0) {
          setIsAuthenticated(true);
          setUser(userDecrypted);
        } else {
          setIsAuthenticated(false);
        }
      }
      if (profileTemp) {
        setProfile(profileTemp);
        mixpanelIdentify(profileTemp);
      }
      if (impersonationRecord) {
        setImpersonation(impersonationRecord);
      }
      if (factorsRecord) {
        setFactors(factorsRecord);
      }
      setIsLoaded(true);
    } catch (_e) {
      clearState();
      window.location.href = '/logout';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run once on mount to hydrate state from localStorage
  }, []);

  // Enforce admin-route access on every navigation
  useEffect(() => {
    if (profile) {
      adminCheck(
        location.pathname.startsWith('/admin'),
        profile,
        impersonation,
      );
    }
  }, [location.pathname, profile, impersonation, adminCheck]);

  const getProfile = async () => {
    try {
      const resp = await callAPIAsync<{
        profile?: Profile;
        factors: Factors;
      }>('/profile', null);
      if (resp.profile) {
        const profileResponse = resp.profile;
        storage.setFactors(resp.factors);
        setFactors(resp.factors);
        storage.setProfile(profileResponse);
        setProfile(resp.profile);
        mixpanelIdentify(resp.profile);
      } else {
        throw new NoProfileError();
      }
    } catch (error) {
      datadogRum.addError(error, { email: user?.email });
      let displayMessage = 'Could not get user profile.  Please try again.';
      if (error instanceof NoProfileError) {
        displayMessage =
          'No profile found. Please contact your Customer Success Manager.';
      }
      displayToastMessage({
        severity: 'error',
        summary: displayMessage,
        detail: '',
      });
      throw new Error('Error getting profile');
    }
  };

  const handleAuthentication = async (
    authenticated: boolean,
    userRecord: DecryptedUser | string,
  ): Promise<void> => {
    setIsAuthenticated(authenticated);
    if (typeof userRecord !== 'string') {
      setUser(userRecord);
    }
    if (authenticated) {
      await getProfile();
      if (window.location.href.indexOf('localhost') === -1) {
        void callAPIAsync('/profile/update', null);
      }
    } else {
      clearState();
    }
  };

  const endImpersonation = () => {
    setImpersonation(null);
    storage.removeImpersonation();
    storage.removeTeamsAPI();
    storage.removeFilters();
    if (window.location.search.length > 0) {
      window.location.href = window.location.pathname;
    } else {
      window.location.reload();
    }
  };

  const scope = getDefaultScope(profile, impersonation);
  const homePage = `/cases?page=1&scope=${scope}`;

  return {
    isAuthenticated,
    user,
    profile,
    impersonation,
    setImpersonation,
    isLoaded,
    factors,
    clearState,
    adminCheck,
    handleAuthentication,
    endImpersonation,
    homePage,
    scope,
  };
}
